import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {StateService} from "@uirouter/core";
import {Observable, zip} from "rxjs";
import * as _ from "lodash";

import {FormsService} from "../forms.service";
import {SettingsService} from "./settings.service";
import {SubtitlesService} from "../subtitles.service";
import {FormStatus, ModuleData, ModuleEditableData} from "../../models";
import {AppAlertService} from "../app-alert.service";
import {ParametersService} from "../parameters.service";
import {UserService} from "../user.service";
import {SendDataService} from "../send-data.service";

@Injectable({
    providedIn: 'root'
})
export class SettingsModuleInfoService {
    private appAlert = inject(AppAlertService);
    private forms = inject(FormsService);
    private http = inject(HttpClient);
    private parameters = inject(ParametersService);
    private sendData = inject(SendDataService);
    private settings = inject(SettingsService);
    private $state = inject(StateService);
    private subtitles = inject(SubtitlesService);
    private user = inject(UserService);
    
    deleteModuleUdid: string | null = null;
    newDefaultModuleId: number | null = null;

    private checkSelectedModuleData(module: ModuleData): boolean {
        let valid = true;
        let errorField = '';

        for (const key in module) {
            if (!(module.postcodePolicyAccepted === false && key === 'zipCode') && key !== 'phoneNumber') {
                const moduleKey = key as keyof ModuleEditableData;
                if (!this.forms.checkModuleData(module, moduleKey)) {
                    valid = false;
                    errorField = key;
                }
            }
        }

        if (!valid) {
            const alert = {
                name: this.subtitles.setSubtitle(3382),
                email: this.subtitles.setSubtitle(1342),
                zipCode: this.subtitles.setSubtitle(3384),
                additionalInformation: this.subtitles.setSubtitle(1643)
            };

            this.appAlert.openAlertDialog({
                title: this.subtitles.setSubtitle(1188),
                message: alert[errorField],
                okButtonText: this.subtitles.setSubtitle(1136),
            });
        }

        return valid;
    }

    changeModuleData(module: ModuleData, formStatus: FormStatus): void {
        if (this.parameters.modules[module.id] === module) return;

        const isDataValid = this.checkSelectedModuleData(module);
        if (!isDataValid) return;

        if (!this.parameters.demoAccount()) {
            this.settings.savedModuleData = true;
        }

        module.gmtTime = this.forms.getGmtTime(module).toString();

        if (!module.postcodePolicyAccepted) {
            module.zipCode = '';
            module.country = '';
        }

        const promises = [];

        if (formStatus.validModuleName || formStatus.validAdditionalInformation) {
            const moduleData = {
                module_name: module.name,
                additional_information: module.additionalInformation ?? ''
            }
            const moduleDataPromise = this.http.put(
                `/api/v1/users/${this.user.user_id}/modules/${this.settings.selectedModule.udid}/info/data`, moduleData);
            promises.push(moduleDataPromise);
        }

        if (
            module.postcodePolicyAccepted && (
                (formStatus.changedPostcodePolicy !== null && formStatus.changedPostcodePolicy !== module.postcodePolicyAccepted) ||
                formStatus.validZipCode ||
                (formStatus.changedCountry !== null && formStatus.changedCountry !== module.country)
            )
        ) {
            const locationData = {
                post_policy_accepted: module.postcodePolicyAccepted,
                zip_code: module.zipCode,
                country_name: module.country,
            }
            const locationPromise = this.http.put(
                `/api/v1/users/${this.user.user_id}/modules/${this.settings.selectedModule.udid}/info/location`, locationData);
            promises.push(locationPromise);
        }

        if (formStatus.changedGmtId !== null && formStatus.changedGmtId !== module.gmtId) {
            const timeZoneData = {
                gmt_id: formStatus.changedGmtId,
            }
            const timeZonePromise = this.http.put(
                `/api/v1/users/${this.user.user_id}/modules/${this.settings.selectedModule.udid}/info/time_zone`, timeZoneData);
            promises.push(timeZonePromise);
        }

        const isSettingDefaultModule =
            this.newDefaultModuleId !== null &&
            this.settings.selectedModule.id === this.newDefaultModuleId;

        if (isSettingDefaultModule) {
            const defaultModuleRequest = this.setDefaultModule(this.settings.selectedModule.udid);
            promises.push(defaultModuleRequest);
        }

        if (promises.length === 0) return;

        zip(promises).subscribe({
            next: () => {
                if (isSettingDefaultModule) {
                    this.parameters.modules.forEach(item => item.default = false);
                    this.settings.modules.forEach(item => item.default = false);
                    module.default = true;
                    this.newDefaultModuleId = null;
                }
                this.settings.savedModuleData = false;
                const parametersModuleIndex = this.parameters.modules.findIndex(item => item.udid === module.udid);
                const settingsModuleIndex = this.settings.modules.findIndex(item => item.udid === module.udid);
                const updatedModule = _.cloneDeep(module);
                updatedModule.gmtTime = String(this.forms.getGmtTime(module));

                this.parameters.modules[parametersModuleIndex] = updatedModule;
                this.settings.modules[settingsModuleIndex] = _.cloneDeep(updatedModule);

                this.appAlert.openAlertDialog({
                    title: this.subtitles.setSubtitle(1188),
                    message: this.subtitles.setSubtitle(2973),
                    okButtonText: this.subtitles.setSubtitle(475),
                });
            },
            error: () => {
                this.appAlert.openAlertDialog({
                    title: this.subtitles.setSubtitle(1188),
                    message: this.subtitles.setSubtitle(1941),
                    okButtonText: this.subtitles.setSubtitle(1136),
                });
            }
        })
    }

    setDefaultModule(udid: string): Observable<void> {
        return this.http.put<void>(`/api/v1/users/${this.user.user_id}/modules/${udid}/default`, {});
    }

    setFirstModuleAsDefaultIfNeeded(modules: ModuleData[]): void {
        const hasDefaultModule = modules.some(module => module.default);
        if (hasDefaultModule || modules.length === 0) return;

        const firstModule = modules[0];
        firstModule.default = true;
        this.parameters.selectedModuleHash = firstModule.udid;
        this.setDefaultModule(firstModule.udid).subscribe({
            error: (error: HttpErrorResponse) => {
                console.error('Error setting default module:', error);
            }
        });
    }

    sendPw(): void  {
        let hash = this.parameters.selectedModuleHash;
        if (this.settings.selectedModule.udid !== this.parameters.selectedModuleHash) {
            hash = this.settings.selectedModule.udid;
        }

        this.http.post(`/api/v1/users/${this.user.user_id}/modules/${hash}/force_data_sync`, {})
            .subscribe({
                next: () => {
                    const message = `${this.subtitles.setSubtitle(624)}. ${this.subtitles.setSubtitle(1326)}`;
                    this.appAlert.openAlertDialog({
                        title: this.subtitles.setSubtitle(734),
                        message,
                        okButtonText: this.subtitles.setSubtitle(475),
                    });
                    this.$state.go('home', {selectedModuleHash: this.parameters.selectedModuleHash})
                },
                error: (response) => {
                    if (response.status === 406) {
                        const timeLeft = response.error.error.time_left;
                        const message = `${this.subtitles.setSubtitle(624)} ${timeLeft} ${this.subtitles.setSubtitle(414).toLowerCase()}.`;
                        this.appAlert.openAlertDialog({
                            title: this.subtitles.setSubtitle(734),
                            message,
                            okButtonText: this.subtitles.setSubtitle(5015),
                        });
                    } else {
                        this.appAlert.openAlertDialog({
                            title: this.subtitles.setSubtitle(734),
                            message: this.subtitles.setSubtitle(1941),
                            okButtonText: this.subtitles.setSubtitle(5015),
                        });
                    }
                }
            });
    }

    deleteModule(): void {
        this.sendData.deleteModule(this.deleteModuleUdid)
            .subscribe(() => {
                this.deleteModuleUdid = null;
                this.$state.go('home', {selectedModuleHash: 0}).then(
                    () => {
                        window.location.reload()
                    }
                );
            });
    }
}
